import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, reduxForm, change } from 'redux-form';
import { TextAreaField } from '../../../../../../components/fields';
import {
    getTimesheetShifts,
    approveTimesheet,
    rejectTimesheet,
    deleteTimeRecord,
    saveTimeEntry,
    resubmitTimesheet,
    confirmTimesheet,
    updateTimesheet,
    getJobProjects
} from '../../../../../../reducers/timekeeping';
import { FormattedMessage } from 'react-intl';
import messages from '../../../../../../text/messages';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import { TIMESHEET_ACTIONS, TIMESHEET_STATUS_ADMIN_UNLOCKED, TIMESHEET_STATUS_LOCKED } from '../../../../../../text/enumerations';
import WorkWeek from '../../../../components/workWeek';

class AdminEditShiftsForm extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            editMode: false
        };

        this.resolveActions = this.resolveActions.bind(this);

        this.reject = this.reject.bind(this);
        this.approve = this.approve.bind(this);
        this.submit = this.submit.bind(this);
        this.confirm = this.confirm.bind(this);
        this.resubmit = this.resubmit.bind(this);
        this.updateShifts = this.updateShifts.bind(this);
        this.toggleEdit = this.toggleEdit.bind(this);
    }

    componentDidMount() {
        const { job, getJobProjects } = this.props;
        getJobProjects(job.id);
    }

    toggleEdit() {
        this.setState({ editMode: !this.state.editMode });
    }

    resolveActions() {
        const { action, cancel, changeField, form, valid } = this.props;

        switch (action) {
            case TIMESHEET_ACTIONS.REVIEW:
                return {
                    actions: {
                        submit: {
                            message: <FormattedMessage {...messages.approve} />,
                            onMouseDown: () => changeField(form, 'action', 'submit')
                        },
                        reject: {
                            onMouseDown: () => changeField(form, 'action', 'reject'),
                            type: 'submit'
                        },
                        cancel: { action: cancel }
                    },
                    submitAction: values => {
                        if (values.action === 'reject' && valid) {
                            this.reject(values);
                        } else if (values.action === 'submit') {
                            this.approve(values);
                        }
                    }
                };

            case TIMESHEET_ACTIONS.CONFIRM:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.submit} /> }, cancel: { action: cancel } },
                    submitAction: values => this.updateShifts(values, this.confirm)
                };

            case TIMESHEET_ACTIONS.EDIT:
                return {
                    actions: { submit: { message: <FormattedMessage {...messages.ok} /> }, cancel: { action: cancel } },
                    submitAction: values => this.updateShifts(values, this.resubmit)
                };

            case TIMESHEET_ACTIONS.VIEW:
            default:
                return {
                    actions: {
                        submit: {
                            message: <FormattedMessage {...messages.close} />,
                            onMouseDown: cancel
                        }
                    }
                };
        }
    }

    submit(values) {
        const { submitAction } = this.resolveActions();
        submitAction(values);
    }

    approve(values) {
        const { timesheet, approveTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            approveTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    reject(values) {
        const { timesheet, rejectTimesheet, updateTimesheet, cancel, reloadData } = this.props;
        const { approver_notes } = values;
        updateTimesheet({ ...timesheet, approver_notes }, () => {
            rejectTimesheet(timesheet.id, approver_notes, () => {
                reloadData();
                cancel();
            });
        });
    }

    resubmit(values) {
        const { worker_notes } = values;
        const { timesheet, resubmitTimesheet, cancel, reloadData, getTimesheetShifts, updateTimesheet } = this.props;

        updateTimesheet({ ...timesheet, worker_notes }, () =>
            resubmitTimesheet(timesheet.id, () => {
                reloadData();
                getTimesheetShifts(timesheet.id);
                cancel();
            })
        );
    }
    confirm(values) {
        const { worker_notes } = values;
        const { timesheet, confirmTimesheet, cancel, reloadData, getTimesheetShifts, updateTimesheet } = this.props;
        confirmTimesheet(timesheet.id, () =>
            updateTimesheet({ ...timesheet, worker_notes }, () => {
                reloadData();
                getTimesheetShifts(timesheet.id);
                cancel();
            })
        );
    }

    updateShifts(values, onComplete) {
        const { shifts, job, saveTimeEntry } = this.props;

        let modified = [];
        shifts.forEach(s => {
            if (values[s.id] && values[s.id].duration !== s.minutes) {
                modified.push({ ...s, ...values[s.id] });
            }
        });

        this.setState({ ...this.state, loading: modified.length }, () => {
            modified.forEach(s => {
                saveTimeEntry({ ...s, time_in: moment.tz(s.time_in, job.work_timezone), jobId: job.id, project_id: s.project_id, minutes: s.duration }, () =>
                    this.setState({ ...this.state, loading: this.state.loading - 1 }, () => {
                        if (this.state.loading === 0 && onComplete) {
                            onComplete(values);
                        }
                    })
                );
            });
        });
    }

    render() {
        const { job, shifts, timesheet, handleSubmit, submitting } = this.props;
        const { editMode } = this.state;
        if (!timesheet || !shifts || !job) return null;

        const { actions } = this.resolveActions();

        return (
            <div className="row">
                <div className="col-xs-12 group">
                    <WorkWeek date={timesheet.period_ending} job={job} timesheet={timesheet} readOnly={!editMode} />
                </div>
                {TIMESHEET_STATUS_ADMIN_UNLOCKED.includes(timesheet.status) && (
                    <div className="col-xs-12 group">
                        <span className="button" onClick={this.toggleEdit}>
                            <FormattedMessage {...(editMode ? messages.close : messages.edit)} />{' '}
                        </span>
                    </div>
                )}
                <form onSubmit={handleSubmit(values => this.submit(values))}>
                    <div className="col-xs-12">
                        <Field
                            {...{
                                component: TextAreaField,
                                placeholder: messages.workerNotes,
                                name: 'worker_notes',
                                disabled: [TIMESHEET_STATUS_LOCKED.includes(timesheet.status)]
                            }}
                        />
                    </div>
                    <div className="col-xs-12">
                        <Field
                            {...{
                                component: TextAreaField,
                                placeholder: messages.approverNotes,
                                name: 'approver_notes',
                                disabled: [TIMESHEET_STATUS_LOCKED.includes(timesheet.status)]
                            }}
                        />
                    </div>
                    <div className="col-xs-12">
                        <WizardButtons disabled={submitting} {...actions} />
                    </div>
                </form>
            </div>
        );
    }
}

AdminEditShiftsForm.defaultProps = {
    action: undefined,
    job: undefined,
    timesheet: undefined,
    shifts: undefined,
    cancel: () => null,
    reloadData: () => null
};
AdminEditShiftsForm.propTypes = {
    action: PropTypes.string.isRequired,
    job: PropTypes.object.isRequired,
    timesheet: PropTypes.object.isRequired,
    shifts: PropTypes.array.isRequired,
    cancel: PropTypes.func.isRequired,
    reloadData: PropTypes.func
};

AdminEditShiftsForm = reduxForm({
    destroyOnUnmount: true,
    enableReinitialize: false,
    forceUnregisterOnUnmount: true,
    keepDirtyOnReinitialize: false
})(AdminEditShiftsForm);

const mapStateToProps = (state, props) => {
    const { timesheet } = props;
    const projects = state.timekeeping.projects;

    return {
        role: state.identity.role,
        projects,
        initialValues: { worker_notes: timesheet.worker_notes, approver_notes: timesheet.approver_notes }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            changeField: change,
            getJobProjects,
            getTimesheetShifts,
            approveTimesheet,
            rejectTimesheet,
            deleteTimeRecord,
            resubmitTimesheet,
            confirmTimesheet,
            updateTimesheet,
            saveTimeEntry
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(AdminEditShiftsForm);



// WEBPACK FOOTER //
// ./src/pages/timekeeping/forms/editShifts/tenants/admin/index.jsx